/*
test case (MT1):
https://localmonkey.com/r2/survey-closed?sm=oJH2uafMAEw6Q2_2BDjw_2F8_2F4yWe0n_2Bf9YMne7Ds_2BgImuhYR7P4vs5XHiMcSDfo0491MTUPtcc3gsDWOwn7HGbCWgzxy0tDUe175EpAzdsgT5Q_3D
 */
import React, { useContext } from 'react';
import { NextPage, Redirect } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
// TODO: consider dropping the usage of BasePage
import BasePage from '@sm/webassets/BasePage';
import { StaticContext } from '@sm/webassets/StaticContext';
import { t, defineMessages } from '@sm/intl';
import { ThemeProvider } from 'react-jss';
import routeHandler from '~helpers/routeHandler';
import surveyClosed, { SurveyClosedServerSideData } from '~helpers/pages/survey-closed';
import fetchLocaleMessages from '~helpers/fetchLocaleMessages';
import BrandedSurveyClosed from '~app/pages/SurveyClosed/BrandedSurveyClosed';
import EUBrandedSurveyClosed from '~app/pages/SurveyClosed/EUBrandedSurveyClosed';
import NonBrandedSurveyClosed from '~app/pages/SurveyClosed/NonBrandedSurveyClosed';
import BrandedEmbeddedSurveyClosed from '~app/pages/SurveyClosed/BrandedEmbeddedSurveyClosed';
import EmbeddedNonBrandedHeader from '~app/components/EmbeddedNonBrandedHeader';

import theme from '~app/styles/cmsTheme';
import { CustomGetServerSideProps, LayoutData } from '~helpers/middleware/types';

const COPY = defineMessages({
  DEFAULT_CLOSED_MESSAGE: {
    id: 'survey-closed.closedMessage',
    defaultMessage:
      'This survey is currently closed.  Please contact the author of this survey for further assistance.',
  },
  META_KEYWORDS: {
    id: 'survey-closed.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  CLOSED_PAGE_TITLE: {
    id: 'survey-closed.title',
    defaultMessage: 'Free Online Survey Software by SurveyMonkey: Closed Survey',
  },
  CLOSED_PAGE_NON_BRANDED_TITLE: {
    id: 'survey-closed.nonBrandedTitle',
    defaultMessage: 'Online Survey Software: Closed Survey',
  },
});

const redirect: Redirect = {
  destination: '/mp/online-research/',
  permanent: true, // 301 status
};

type ServerSideProps = LayoutData & SurveyClosedServerSideData;

export const getServerSideProps: CustomGetServerSideProps<ServerSideProps> = async ({ req, res, query }) => {
  const handler = routeHandler({ query, pageDataLoader: surveyClosed, owners: '@webplatform/content' });
  if (!query?.sm) {
    return { redirect }; // redirect without SMParam
  }

  await handler.run(req, res);

  if (!req?.pageProps?.collectorData?.collector?.id) {
    return { redirect };
  }

  // TODO: Retire the usage of StaticContext and use page props instead.
  // Additional page-specific (reCAPTCHA) data needed in StaticContext
  req.addClientPayload('staticData', {
    recaptchaV3: {
      enabled: process?.env?.SECURITY_RECAPTCHA_V3_ENABLED?.toLowerCase() === 'true' ?? false,
      siteKey: process?.env?.SECURITY_RECAPTCHA_V3_SITE_KEY ?? '',
      url: process?.env?.SECURITY_RECAPTCHA_V3_URL ?? '',
      urlChina: process?.env?.SECURITY_RECAPTCHA_V3_URL_CHINA ?? '',
      useEnterprise: process?.env?.SECURITY_RECAPTCHA_V3_USE_ENTERPRISE?.toLowerCase() === 'true' ?? false,
    },
  });

  const { slLanguageLocale, languageCode } = req.payloads?.staticData?.environment ?? {};
  const translationData = await fetchLocaleMessages(slLanguageLocale, languageCode);

  return {
    props: {
      // added to the request via routeHandler
      ...req.payloads,
      // added to the request via the pageDataLoaderFactory
      ...req.pageProps,
      fetchServerSideDataSuccess: true,
      translationData,
      layout: {
        variant: 'SurveyMonkey',
        options: {
          actionFlow: 'SurveyClosed',
          pageId: 'Survey Closed',
          legacyWeb: 'anonweb',
          includeHeader: false,
          includeFooter: !req?.pageProps?.pageOptions?.isNonBrandedSmParam,
          includeGTM: true,
        },
      },
    },
  };
};

type SurveyClosedBodyProps = {
  defaultMessage: string;
  isFetchSuccess: boolean;
  isEmbedded: boolean | undefined;
  isEUSubdomain: boolean | undefined;
  isBrandingDisabled: boolean | undefined;
};

const SurveyClosedBody = ({
  defaultMessage,
  isFetchSuccess,
  isEmbedded,
  isBrandingDisabled,
  isEUSubdomain,
}: SurveyClosedBodyProps): React.ReactElement => {
  if (!isFetchSuccess) {
    return <NonBrandedSurveyClosed message={t(COPY.DEFAULT_CLOSED_MESSAGE)} />;
  }
  if (isEmbedded) {
    return isBrandingDisabled ? (
      <EmbeddedNonBrandedHeader message={defaultMessage} icon="closed" />
    ) : (
      <BrandedEmbeddedSurveyClosed message={defaultMessage} />
    );
  }
  if (isBrandingDisabled) {
    return <NonBrandedSurveyClosed message={defaultMessage} />;
  }
  if (isEUSubdomain) {
    return <EUBrandedSurveyClosed message={defaultMessage} />;
  }

  return <BrandedSurveyClosed message={defaultMessage} />;
};

const SurveyClosed: NextPage<ServerSideProps> = ({
  fetchServerSideDataSuccess = false,
  pageOptions,
  collectorData,
}) => {
  // TODO: the majority of the data re-/assignments can take place before the rendering starts.
  // we're keeping the following as an incremental step from the existing logic.
  const { canonical: canonicalName, isNonBrandedSmParam = false, isEmbeddedSmParam = false } = pageOptions || {};
  const { locale } = useContext(StaticContext);
  const { pathname } = useRouter();
  const canonical = `${canonicalName}${pathname}`;
  const { collector } = collectorData || {};
  const { closedMessage } = collector?.closedPage || {};
  const isEmbedded = isEmbeddedSmParam || collector?.type?.toUpperCase() === 'POPUP';

  const isBrandingDisabled =
    isNonBrandedSmParam || collector?.closedPage?.isBrandingDisabled || collector?.hideBranding;

  const showFooter = fetchServerSideDataSuccess && !isBrandingDisabled && !isEmbedded;

  const title =
    collector && !isBrandingDisabled && !locale.isEUSubdomain
      ? t(COPY.CLOSED_PAGE_TITLE)
      : t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE);

  return (
    <ThemeProvider theme={theme}>
      <BasePage
        includeHeader={false}
        includeFooter={showFooter}
        pageId="Survey Closed"
        legacyWeb="anonweb"
        includeExperiments={false}
        includeGTM={
          false /* set in page.props.layout.options.includeGTM instead since we're moving away from BasePage */
        }
      >
        <Head>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_CLOSED_MESSAGE)} />
          <link rel="canonical" href={canonical} />
          <title>{title}</title>
        </Head>
        <SurveyClosedBody
          defaultMessage={closedMessage || t(COPY.DEFAULT_CLOSED_MESSAGE)}
          isFetchSuccess={fetchServerSideDataSuccess && collector !== null}
          isBrandingDisabled={isBrandingDisabled}
          isEUSubdomain={locale.isEUSubdomain}
          isEmbedded={isEmbedded}
        />
      </BasePage>
    </ThemeProvider>
  );
};
export default SurveyClosed;
